import { useMutation } from '@apollo/client';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  LinearProgress,
  DialogActions,
  Button,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { VendorOpportunity, PropertyToExcludeFromDealInfoVoSyncInput } from '@trnsact/trnsact-shared-types';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { useState, useEffect } from 'react';
import { SYNC_DEAL_INFO_TO_VO } from './queres';

function renderSyncDataByProperty(
  data: any,
  level: number = 0,
  importHasRun: boolean = false,
  excludedProperties: Set<string> = new Set(),
  dataName: string | null = null,
  onToggle?: (name: string, dataName: string | null) => void
) {
  if (!data) return null;
  if (Array.isArray(data)) {
    return data.map((item, idx) => (
      <Box key={idx} sx={{ pl: level * 2, borderLeft: level ? '2px solid #eee' : undefined, mb: 1 }}>
        {renderSyncDataByProperty(item, level, importHasRun, excludedProperties, dataName, onToggle)}
      </Box>
    ));
  }

  const isProperty = data.valueBeforeSync !== null || data.valueAfterSync !== null;
  const isExcluded = isProperty && excludedProperties.has([data.name, dataName].join(':::'));

  return (
    <Box
      sx={{
        pl: level * 2,
        borderLeft: level ? '2px solid #eee' : undefined,
        mb: 1,
        opacity: isExcluded ? 0.4 : 1,
        transition: 'opacity 0.2s ease',
        pointerEvents: isExcluded ? 'none' : undefined,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pointerEvents: 'auto' }}>
        {isProperty && (
          <FormControlLabel
            control={
              <Switch
                checked={!excludedProperties.has([data.name, dataName].join(':::'))}
                onChange={() => onToggle?.(data.name, dataName)}
                disabled={importHasRun}
                size="small"
              />
            }
            label={
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {data.name}
              </Typography>
            }
          />
        )}
        {!isProperty && (
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {data.name}
          </Typography>
        )}
      </Box>
      {data.message && (
        <Typography variant="body2" color="text.secondary">
          {data.message}
        </Typography>
      )}
      {isProperty && (
        <Box sx={{ mt: 0.5, mb: 0.5, pl: 5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center' }}>
            <Typography variant="caption" sx={{ width: 220, fontWeight: 500, color: 'text.secondary' }}>
              {importHasRun ? 'Previous Value' : 'Current Value'}
            </Typography>
            <Typography variant="caption" sx={{ width: 110, fontWeight: 500, color: 'text.secondary' }}>
              {importHasRun ? 'New Value' : 'Value After Sync'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center' }}>
            <Typography variant="body2" sx={{ width: 220, fontFamily: 'monospace' }}>
              {String(data.valueBeforeSync === '' ? 'N/A' : data.valueBeforeSync)}
            </Typography>
            <Typography variant="body2" sx={{ width: 110, fontFamily: 'monospace' }}>
              {excludedProperties.has([data.name, dataName].join(':::')) ? (
                <span>
                  <span style={{ textDecoration: 'line-through' }}>
                    {String(data.valueAfterSync === '' ? 'N/A' : data.valueAfterSync)} 
                  </span>
                  (Will not be synced)
                </span>
              ) : (
                String(data.valueAfterSync === '' ? 'N/A' : data.valueAfterSync)
              )}
            </Typography>
          </Box>
        </Box>
      )}
      {data.syncDataByProperty &&
        renderSyncDataByProperty(
          data.syncDataByProperty,
          level + 1,
          importHasRun,
          excludedProperties,
          data.name,
          onToggle
        )}
    </Box>
  );
}

const SyncDealInfoDialog = ({ vo, externalDealId }: { vo: VendorOpportunity; externalDealId: string }) => {
  const { isOpen, handleClose, data } = useModal(ModalsKeys.SyncDealInfoDialog);
  const [isImporting, setIsImporting] = useState(false);
  const [previewResult, setPreviewResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [runPreview, { loading: previewLoading }] = useMutation(SYNC_DEAL_INFO_TO_VO);
  const [runImport, { loading: importLoading }] = useMutation(SYNC_DEAL_INFO_TO_VO);
  const [importHasRun, setImportHasRun] = useState(false);
  const [excludedProperties, setExcludedProperties] = useState<Set<string>>(new Set());

  const handleToggleProperty = (name: string, dataName: string | null = null) => {
    setExcludedProperties(prev => {
      const next = new Set(prev);
      const key: string = [name, dataName].join(':::');
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  useEffect(() => {
    if (isOpen && vo?.vendorOpportunityId) {
      setPreviewResult(null);
      setError(null);
      setExcludedProperties(new Set<string>());
      runPreview({
        variables: {
          input: {
            externalDealId,
            vendorOpportunityId: vo.vendorOpportunityId,
            isPreview: true,
          },
        },
      })
        .then(({ data }) => {
          setPreviewResult(data?.syncDealInfoToVo);
        })
        .catch(e => setError(e.message));
    }
    // eslint-disable-next-line
  }, [isOpen, vo?.vendorOpportunityId, externalDealId]);

  const handleImport = async () => {
    setIsImporting(true);
    setError(null);
    try {
      const propertiesToExcludeFromSync: PropertyToExcludeFromDealInfoVoSyncInput[] = Array.from(
        excludedProperties
      ).map(key => {
        const [propertyName, dataName] = key.split(':::');
        return { propertyName, dataName };
      });
      const { data } = await runImport({
        variables: {
          input: {
            externalDealId,
            vendorOpportunityId: vo.vendorOpportunityId,
            isPreview: false,
            propertiesToExcludeFromSync,
          },
        },
      });
      // Show the import result and success message
      setPreviewResult(data?.syncDealInfoToVo);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsImporting(false);
      setImportHasRun(true);
    }
  };

  const handleCancel = () => {
    setPreviewResult(null);
    setError(null);
    setIsImporting(false);
    handleClose();
    window.location.reload();
  };

  const isLoading = previewLoading || isImporting || importLoading;

  return (
    <Dialog open={isOpen} onClose={handleCancel} fullWidth maxWidth="md">
      <DialogTitle>Import Confirmation: Deal ID: {externalDealId}</DialogTitle>
      <DialogContent dividers>
        {isLoading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <LinearProgress sx={{ width: '100%', mb: 2 }} />
            <Typography variant="body1">
              {isImporting ? 'Importing deal information...' : 'Fetching deal information.'}
            </Typography>
            {previewResult && !isImporting && (
              <Box sx={{ mt: 2, width: '100%' }}>
                {renderSyncDataByProperty(
                  previewResult.syncDataByProperty,
                  0,
                  importHasRun,
                  excludedProperties,
                  previewResult.name,
                  handleToggleProperty
                )}
              </Box>
            )}
          </Box>
        )}
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        {previewResult && !isLoading && (
          <>
            {previewResult.previewOnly === false ? (
              <Typography variant="body2" color="success.main" sx={{ mb: 2 }}>
                Deal has been successfully imported!
              </Typography>
            ) : (
              <>
                <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
                  This deal has not yet been synced. Click <b>"Import Deal"</b> to confirm you wish to import these
                  values.
                </Typography>
                <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
                  Importing this deal will overwrite any existing Equipment and Invoice/Sale details currently in this
                  deal record. This action cannot be undone.
                </Typography>
                <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
                  You can choose to exclude specific properties from being synced by using the toggle near each property.
                </Typography>
              </>
            )}
            <Box sx={{ mb: 2 }}>
              {renderSyncDataByProperty(
                previewResult.syncDataByProperty,
                0,
                importHasRun,
                excludedProperties,
                previewResult.name,
                handleToggleProperty
              )}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={isLoading}>
          {importHasRun ? 'Close' : 'Cancel'}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleImport}
          disabled={isLoading || !previewResult || previewResult?.previewOnly === false}
        >
          Import Deal
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SyncDealInfoDialog;
